import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type TransitionEvent,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { radii, space } from '../../../../styles/onboardingUiTokens';
import { SvgIcon } from '../icons/SvgIcon';

const defaultOverlay = `color-mix(in srgb, ${colors.dark} 50%, transparent)`;
const defaultSheetRadius = '1.25rem';
const defaultMaxHeight = 'min(95dvh, 100% - env(safe-area-inset-bottom, 0px))';
const defaultSheetMaxWidth = '42rem';
const defaultMinHeight = '40dvh';

const reducedMotionTransition = css`
  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms !important;
  }
`;

const Layer = styled.div<{ $z: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $z }) => $z};
  pointer-events: none;
`;

const Backdrop = styled.button<{ $visible: boolean; $color: string }>`
  position: fixed;
  inset: 0;
  margin: 0;
  padding: 0;
  border: none;
  cursor: pointer;
  pointer-events: auto;
  background: ${({ $color }) => $color};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 240ms ease;
  ${reducedMotionTransition}

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const Sheet = styled.div<{
  $visible: boolean;
  $bg: string;
  $radius: string;
  $maxH: string;
  $minH: string;
  $sheetMaxW: string;
  $dragY: number;
  $isDragging: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: ${({ $sheetMaxW }) => $sheetMaxW};
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  min-height: ${({ $minH }) => $minH};
  max-height: ${({ $maxH }) => $maxH};
  border-radius: ${({ $radius }) => `${$radius} ${$radius} 0 0`};
  background: ${({ $bg }) => $bg};
  box-shadow: 0 -8px 32px color-mix(in srgb, ${colors.dark} 12%, transparent);
  pointer-events: auto;
  transform: translateY(calc(${({ $visible }) => ($visible ? 0 : 100)}% + ${({ $dragY }) => $dragY}px));
  transition: ${({ $isDragging }) => ($isDragging ? 'none' : 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)')};
  will-change: transform;
  ${reducedMotionTransition}
`;

const HandleStrip = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 2.75rem;
  padding-top: ${space.sm};
  padding-bottom: ${space.xs};
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const DragPill = styled.span`
  width: 2.25rem;
  height: 0.3125rem;
  border-radius: ${radii.pill};
  background: ${colors.light};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${space.sm};
  right: ${space.sm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: ${space.xs};
  border: none;
  border-radius: ${radii.md};
  background: transparent;
  color: ${colors.dark};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const SheetInner = styled.div<{ $padInline: string; $padBottom: string; $gap: string }>`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap};
  padding-left: ${({ $padInline }) => $padInline};
  padding-right: ${({ $padInline }) => $padInline};
  padding-bottom: ${({ $padBottom }) => $padBottom};
  padding-top: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SheetChrome = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

const Footer = styled.div<{ $padInline: string }>`
  flex: 0 0 auto;
  background: ${colors.white};
  border-top: 1.5px solid ${getMediumRGBA(0.15)};
  padding: ${space.md} ${({ $padInline }) => $padInline};
  padding-bottom: calc(${space.md} + env(safe-area-inset-bottom, 0px));
`;

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector));
}

export type BottomDrawerProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Backdrop fill (default: semi-transparent dark using palette). */
  overlayColor?: string;
  /** Sheet surface color. */
  sheetBackgroundColor?: string;
  /** Inner horizontal padding (CSS); default one step above `contentPadding` (scroll area). */
  contentPaddingInline?: string;
  /** Inner bottom padding (CSS) under the scroll area; top padding stays 0 below the handle. */
  contentPadding?: string;
  /** Vertical rhythm between chrome and scrollable content (CSS `gap`). */
  gap?: string;
  /** Max height of the sheet (CSS), e.g. `70vh`. */
  maxHeight?: string;
  /** Minimum height of the sheet (CSS). */
  minHeight?: string;
  /** Horizontal cap for the sheet; centered when the viewport is wider (CSS). */
  sheetMaxWidth?: string;
  /** Top corner radius (CSS). */
  sheetBorderRadius?: string;
  /** Close control graphic; keep meaningful name on the button (`aria-label`). */
  closeIcon?: ReactNode;
  /** Drag must exceed this fraction of sheet height to dismiss (default 0.15). */
  dismissDragFraction?: number;
  /** Stacking order for the portal root. */
  zIndex?: number;
  /**
   * Pinned below the scroll area, inside the sheet — for a drawer long enough that the header's
   * close control scrolls out of reach (Figma `Button Wrapper`). Omit for the compact drawers.
   */
  footer?: ReactNode;
};

export function BottomDrawer({
  open,
  onClose,
  children,
  overlayColor = defaultOverlay,
  sheetBackgroundColor = colors.white,
  contentPaddingInline = space.lg,
  contentPadding = space.md,
  gap = space.sm,
  maxHeight = defaultMaxHeight,
  minHeight = defaultMinHeight,
  sheetMaxWidth = defaultSheetMaxWidth,
  sheetBorderRadius = defaultSheetRadius,
  closeIcon,
  dismissDragFraction = 0.15,
  zIndex = 10_000,
  footer,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  onKeyDown: onKeyDownProp,
  ...rest
}: BottomDrawerProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const [mounted, setMounted] = useState(open);
  const [animateIn, setAnimateIn] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dragStartRef = useRef<{ y: number; sheetHeight: number } | null>(null);
  const dragYRef = useRef(0);
  const pointerIdRef = useRef<number | null>(null);

  const effectiveAriaLabel = ariaLabelledBy ? undefined : (ariaLabel ?? 'Schublade');
  const portalActive = mounted || open;

  useEffect(() => {
    let cancelled = false;
    let rafMount = 0;
    let rafAnimate = 0;
    let rafClose = 0;

    if (open) {
      rafMount = window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        setMounted(true);
        rafAnimate = window.requestAnimationFrame(() => {
          if (cancelled) {
            return;
          }
          setAnimateIn(true);
        });
      });
    } else {
      rafClose = window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        setAnimateIn(false);
      });
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafMount);
      window.cancelAnimationFrame(rafAnimate);
      window.cancelAnimationFrame(rafClose);
    };
  }, [open]);

  useEffect(() => {
    if (!portalActive) {
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [portalActive]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocKeyDown = (ev: globalThis.KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open && mounted && animateIn) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      window.setTimeout(() => {
        closeRef.current?.focus();
      }, 0);
    }
  }, [open, mounted, animateIn]);

  const finalizeUnmountIfClosed = useCallback(() => {
    if (!open && !animateIn) {
      setMounted(false);
      setDragY(0);
      dragYRef.current = 0;
      setIsDragging(false);
      dragStartRef.current = null;
      previouslyFocused.current?.focus?.({ preventScroll: true });
      previouslyFocused.current = null;
    }
  }, [open, animateIn]);

  const onSheetTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      finalizeUnmountIfClosed();
    },
    [finalizeUnmountIfClosed],
  );

  const endDrag = useCallback(
    (ev?: ReactPointerEvent) => {
      const start = dragStartRef.current;
      if (pointerIdRef.current !== null && ev?.pointerId !== undefined && ev.pointerId !== pointerIdRef.current) {
        return;
      }
      if (ev?.currentTarget instanceof HTMLElement && pointerIdRef.current !== null) {
        try {
          ev.currentTarget.releasePointerCapture(pointerIdRef.current);
        } catch {
          /* ignore */
        }
      }
      pointerIdRef.current = null;
      setIsDragging(false);

      if (start) {
        const threshold = Math.max(1, start.sheetHeight * dismissDragFraction);
        if (dragYRef.current > threshold) {
          onClose();
        }
      }
      dragStartRef.current = null;
      dragYRef.current = 0;
      setDragY(0);
    },
    [dismissDragFraction, onClose],
  );

  const onHandlePointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (ev.button !== 0) {
      return;
    }
    const sheet = sheetRef.current;
    const h = sheet?.getBoundingClientRect().height ?? 0;
    dragStartRef.current = { y: ev.clientY, sheetHeight: h };
    pointerIdRef.current = ev.pointerId;
    setIsDragging(true);
    ev.currentTarget.setPointerCapture(ev.pointerId);
  };

  const onHandlePointerMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current || pointerIdRef.current !== ev.pointerId) {
      return;
    }
    const delta = Math.max(0, ev.clientY - dragStartRef.current.y);
    dragYRef.current = delta;
    setDragY(delta);
  };

  const onHandlePointerUp = (ev: ReactPointerEvent<HTMLDivElement>) => {
    endDrag(ev);
  };

  const onHandlePointerCancel = (ev: ReactPointerEvent<HTMLDivElement>) => {
    endDrag(ev);
  };

  const onDialogKeyDown = (ev: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDownProp?.(ev);
    if (ev.key !== 'Tab' || !sheetRef.current || ev.defaultPrevented) {
      return;
    }
    const nodes = getFocusableElements(sheetRef.current);
    if (nodes.length === 0) {
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  };

  const defaultCloseGraphic = <SvgIcon name="close" size={22} primaryColor={colors.dark} decorative />;

  if (!portalActive || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Layer $z={zIndex} data-bottom-drawer-root>
      <Backdrop
        type="button"
        $visible={animateIn}
        $color={overlayColor}
        aria-label="Dialog schließen"
        tabIndex={-1}
        onClick={onClose}
      />
      <Sheet
        {...rest}
        ref={sheetRef}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-label={effectiveAriaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        onKeyDown={onDialogKeyDown}
        onTransitionEnd={onSheetTransitionEnd}
        $visible={animateIn}
        $bg={sheetBackgroundColor}
        $radius={sheetBorderRadius}
        $maxH={maxHeight}
        $minH={minHeight}
        $sheetMaxW={sheetMaxWidth}
        $dragY={dragY}
        $isDragging={isDragging}
      >
        <SheetChrome>
          <HandleStrip
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerCancel}
          >
            <DragPill aria-hidden />
          </HandleStrip>
          <CloseButton ref={closeRef} type="button" aria-label="Schließen" onClick={() => onClose()}>
            {closeIcon ?? defaultCloseGraphic}
          </CloseButton>
        </SheetChrome>
        {/*
          `contentPadding` applies with a footer too. It used to be forced to 0 in that case, on the
          reasoning that the footer brings its own padding — but that padding sits inside the footer,
          above the button, and does nothing to keep the last piece of content off the footer's top
          divider. The result was content butting straight into the rule.
        */}
        <SheetInner $padInline={contentPaddingInline} $padBottom={contentPadding} $gap={gap}>
          {children}
        </SheetInner>
        {footer ? <Footer $padInline={contentPaddingInline}>{footer}</Footer> : null}
      </Sheet>
    </Layer>,
    document.body,
  );
}
